<template>
  <div class="max-w-3xl mx-auto space-y-3 py-3">
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <div class="flex items-center justify-between mb-2">
        <div>
          <h2 class="text-base font-semibold text-gray-800">AI Assistant</h2>
          <p class="text-xs text-gray-500">
            Let staff chat with an AI assistant that can look up school data (students, classes, results, billing).
          </p>
        </div>
        <ToggleSwitch v-model="form.aiAssistantEnabled" data-testid="ai-master-toggle" />
      </div>

      <Divider />

      <div class="flex items-center justify-between" :class="{ 'opacity-50': !form.aiAssistantEnabled }">
        <div>
          <h3 class="text-sm font-medium text-gray-800">Student access</h3>
          <p class="text-xs text-gray-500">
            Also let students use the assistant. Students only ever see their own data.
          </p>
        </div>
        <ToggleSwitch v-model="form.aiStudentAccess" :disabled="!form.aiAssistantEnabled"
          data-testid="ai-student-toggle" />
      </div>

      <div class="bg-blue-50 border border-blue-200 rounded-lg p-2.5 mt-3">
        <div class="flex items-center gap-1.5">
          <i class="pi pi-info-circle text-blue-600 text-sm"></i>
          <span class="text-xs text-blue-800">
            AI can make mistakes — users are reminded to verify important information.
          </span>
        </div>
      </div>
    </div>

    <div class="flex justify-end">
      <Button label="Save" icon="pi pi-check" size="small" :loading="saving" data-testid="ai-settings-save"
        @click="save" />
    </div>

    <!--
      Usage snapshot lives under the toggles: this tab is where an admin already
      manages the assistant, so it is the natural home for "how much have we
      used". Renders nothing when the backend returns no snapshot.
    -->
    <AiUsagePanel />
  </div>
</template>

<script setup lang="ts">
import { useMutation, useQuery } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import AiUsagePanel from './AiUsagePanel.vue';
import {
  AI_ASSISTANT_SCHOOL_FLAGS,
  UPDATE_SCHOOL_AI_SETTINGS,
  type AiAssistantSchoolFlagsResult,
  type UpdateSchoolAiSettingsResult,
  type UpdateSchoolAiSettingsVariables,
} from '@/graphql/aiAssistant';

/**
 * Admin settings surface for the two per-school AI flags (achievable E4/D1).
 * Lives as a Settings tab alongside MatricNoConfig/PdfResultConfig.
 */
const toast = useToast();

const form = ref({ aiAssistantEnabled: false, aiStudentAccess: false });

const { result, refetch } = useQuery<AiAssistantSchoolFlagsResult>(AI_ASSISTANT_SCHOOL_FLAGS, null, {
  fetchPolicy: 'network-only',
});

watch(result, (val) => {
  const school = val?.getSchool;
  if (school) {
    form.value = {
      aiAssistantEnabled: !!school.aiAssistantEnabled,
      aiStudentAccess: !!school.aiStudentAccess,
    };
  }
}, { immediate: true });

// Master off forces student access off — mirrors the server-side gating matrix (D4).
watch(() => form.value.aiAssistantEnabled, (enabled) => {
  if (!enabled) form.value.aiStudentAccess = false;
});

const { mutate: updateAiSettings, loading: saving } = useMutation<
  UpdateSchoolAiSettingsResult,
  UpdateSchoolAiSettingsVariables
>(UPDATE_SCHOOL_AI_SETTINGS);

async function save() {
  try {
    await updateAiSettings({
      aiAssistantEnabled: form.value.aiAssistantEnabled,
      aiStudentAccess: form.value.aiStudentAccess,
    });
    await refetch();
    toast.add({ severity: 'success', summary: 'Saved', detail: 'AI assistant settings updated.', life: 3000 });
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: e instanceof Error ? e.message : 'Failed to save AI assistant settings.',
      life: 3000,
    });
  }
}
</script>
